import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { ApexOptions } from "apexcharts";
import ReactApexChart from "react-apexcharts";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useFetchProjectsQuery } from "../../store/api/projectsApi";
import { useFetchRequestsQuery } from "../../store/api/requestsApi";
import { useFetchClientsQuery } from "../../store/api/clientsApi";
import { useFetchEmployeesQuery } from "../../store/api/employeesApi";
import { useFetchReviewsQuery } from "../../store/api/reviewsApi";

import { getApiErrorMessage } from "../../store/api/baseApi";
import { getProjectProgressSummary, getTaskHealth, getTaskDelayDays } from "./adminData";
import "./admin.css";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const extractFirstNumber = (str: string): number => {
  const match = str.replace(/,/g, "").match(/[\d.]+/);
  return match ? parseFloat(match[0]) : 0;
};

const parseBudget = (area: string, rate: string): number => {
  const areaSqft = extractFirstNumber(area);
  const ratePerSqft = extractFirstNumber(rate);
  const result = areaSqft * ratePerSqft;
  return Number.isFinite(result) ? result : 0;
};

const formatCrore = (amount: number): string => {
  if (amount >= 10_000_000) return `₹${(amount / 10_000_000).toFixed(2)} Cr`;
  if (amount >= 100_000) return `₹${(amount / 100_000).toFixed(2)} L`;
  if (amount >= 1_000) return `₹${(amount / 1_000).toFixed(1)} K`;
  return `₹${Math.round(amount)}`;
};

const getStatusBadge = (status: string) => {
  if (status === "completed") return "text-bg-success";
  if (status === "in-progress") return "text-bg-primary";
  return "text-bg-warning";
};

const getRequestStatusBadge = (status: string) => {
  if (status === "completed") return "text-bg-success";
  if (status === "in-progress") return "text-bg-primary";
  if (status === "pending") return "text-bg-warning";
  return "text-bg-secondary";
};

const truncate = (str: string, max: number) =>
  str.length > max ? str.slice(0, max) + "…" : str;

const getInitials = (name: string) =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: string | number;
  sub?: string;
  icon: string;
  color: string;
  flat?: boolean;
  masked?: boolean;
  onToggleMask?: () => void;
}> = ({ label, value, sub, icon, color, flat = false, masked, onToggleMask }) => (
  <article className={`dash-stat-card dash-stat-card--${color}${flat ? " dash-stat-card--flat" : ""}`}>
    {onToggleMask && (
      <button
        type="button"
        className="dash-stat-mask-btn"
        onClick={onToggleMask}
        title={masked ? "Show value" : "Hide value"}
      >
        <i className={`bi bi-eye${masked ? "" : "-slash"}`}></i>
      </button>
    )}
    {flat ? (
      <>
        <div className="dash-stat-flat-top">
          <div className="dash-stat-icon"><i className={icon}></i></div>
          <h3 className="dash-stat-value">{masked ? "₹ •••••" : value}</h3>
        </div>
        <div className="dash-stat-body">
          <p className="dash-stat-label">{label}</p>
          {sub && <span className="dash-stat-sub">{masked ? "••••••" : sub}</span>}
        </div>
      </>
    ) : (
      <>
        <div className="dash-stat-top">
          <div className="dash-stat-icon"><i className={icon}></i></div>
          <h3 className="dash-stat-value">{value}</h3>
        </div>
        <div className="dash-stat-body">
          <p className="dash-stat-label">{label}</p>
          {sub && <span className="dash-stat-sub">{sub}</span>}
        </div>
      </>
    )}
  </article>
);

// ─── Progress Bar ─────────────────────────────────────────────────────────────

const ProgressBar: React.FC<{
  pct: number;
  variant?: string;
  size?: "sm" | "md" | "xs";
  label: string;
}> = ({ pct, variant = "bg-success", size = "md", label }) => {
  const safePct = Math.min(100, Math.max(0, Math.round(pct)));
  return (
    /* aria-hidden: percentage is shown as visible text adjacent to every bar */
    <div
      className={`dash-progress dash-progress--${size}`}
      aria-hidden="true"
      title={label}
    >
      <div
        className={`dash-progress-bar ${variant}`}
        style={{ "--dash-bar-w": `${safePct}%` } as React.CSSProperties}
      />
    </div>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const AdminDashboard: React.FC = () => {
  const { data: projects = [], isLoading: loadingProjects, error: projectsError } = useFetchProjectsQuery();
  const { data: requests = [], isLoading: loadingRequests } = useFetchRequestsQuery();
  const { data: clients = [], isLoading: loadingClients } = useFetchClientsQuery();
  const { data: employees = [], isLoading: loadingEmployees } = useFetchEmployeesQuery();
  const { data: reviews = [], isLoading: loadingReviews } = useFetchReviewsQuery();

  const errorMessage = projectsError ? getApiErrorMessage(projectsError as FetchBaseQueryError) : "";

  // ── Derived stats ──────────────────────────────────────────────────────────

  const allTasks = useMemo(() => projects.flatMap((p) => p.progressTasks), [projects]);

  const delayedTasks = useMemo(
    () => allTasks.filter((t) => getTaskHealth(t) === "delayed"),
    [allTasks]
  );

  const completedProjects = useMemo(
    () => projects.filter((p) => p.status === "completed").length,
    [projects]
  );
  const inProgressProjects = useMemo(
    () => projects.filter((p) => p.status === "in-progress").length,
    [projects]
  );
  const pendingProjects = useMemo(
    () => projects.filter((p) => p.status === "pending").length,
    [projects]
  );

  const totalBudget = useMemo(
    () => projects.reduce((sum, p) => sum + parseBudget(p.area, p.rateOfConstruction), 0),
    [projects]
  );

  // ── Progress Chart ────────────────────────────────────────────────────────

  const progressChartData = useMemo(() => {
    const active = projects.filter((p) => p.progressTasks.length > 0).slice(0, 10);
    return {
      labels: active.map((p) => truncate(p.title, 18)),
      planned: active.map((p) => getProjectProgressSummary(p).averagePlanned),
      actual: active.map((p) => getProjectProgressSummary(p).averageActual),
    };
  }, [projects]);

  const progressChartOptions: ApexOptions = {
    chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
    colors: ["#3b82f6", "#22c55e"],
    plotOptions: { bar: { horizontal: false, columnWidth: "55%", borderRadius: 5 } },
    dataLabels: { enabled: false },
    xaxis: {
      categories: progressChartData.labels,
      labels: { rotate: -30, style: { fontSize: "11px", colors: "#667085" } },
    },
    yaxis: { max: 100, labels: { formatter: (v) => `${v}%` } },
    legend: { position: "top" },
    grid: { borderColor: "#f1f5f9" },
    tooltip: { y: { formatter: (v) => `${v}%` } },
  };

  const progressChartSeries = [
    { name: "Planned %", data: progressChartData.planned },
    { name: "Actual %", data: progressChartData.actual },
  ];

  // ── Status Donut ──────────────────────────────────────────────────────────

  const statusDonutOptions: ApexOptions = {
    chart: { type: "donut", fontFamily: "inherit" },
    colors: ["#f59e0b", "#3b82f6", "#22c55e"],
    labels: ["Pending", "In Progress", "Completed"],
    legend: { position: "bottom", fontSize: "12px" },
    plotOptions: { pie: { donut: { size: "65%" } } },
    dataLabels: { enabled: false },
    tooltip: { y: { formatter: (v) => `${v} project${v !== 1 ? "s" : ""}` } },
  };
  const statusDonutSeries = [pendingProjects, inProgressProjects, completedProjects];

  // ── Budget / P&L Chart ────────────────────────────────────────────────────

  const budgetProjects = useMemo(
    () =>
      projects
        .map((p) => {
          const budget = parseBudget(p.area, p.rateOfConstruction);
          const summary = getProjectProgressSummary(p);
          return {
            title: truncate(p.title, 18),
            budget,
            earned: budget * (summary.averageActual / 100),
            status: p.status,
          };
        })
        .filter((p) => p.budget > 0)
        .slice(0, 8),
    [projects]
  );

  const earnedBudget = useMemo(
    () => budgetProjects.reduce((sum, p) => sum + p.earned, 0),
    [budgetProjects]
  );

  const overallPct = totalBudget > 0 ? Math.round((earnedBudget / totalBudget) * 100) : 0;

  const plChartOptions: ApexOptions = {
    chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
    colors: ["#6366f1", "#22c55e"],
    plotOptions: { bar: { horizontal: true, barHeight: "55%", borderRadius: 4 } },
    dataLabels: { enabled: false },
    xaxis: {
      categories: budgetProjects.map((p) => p.title),
      labels: { formatter: (v) => formatCrore(Number(v)), style: { fontSize: "11px" } },
    },
    yaxis: { labels: { style: { fontSize: "11px", colors: "#667085" } } },
    legend: { position: "top" },
    grid: { borderColor: "#f1f5f9" },
    tooltip: { y: { formatter: (v) => formatCrore(Number(v)) } },
  };

  const plChartSeries = [
    { name: "Contract Value", data: budgetProjects.map((p) => Math.round(p.budget)) },
    { name: "Earned Value", data: budgetProjects.map((p) => Math.round(p.earned)) },
  ];

  // ── Delayed Projects ──────────────────────────────────────────────────────

  const delayedProjects = useMemo(
    () =>
      projects
        .map((p) => {
          const delayed = p.progressTasks.filter((t) => getTaskHealth(t) === "delayed");
          const maxDelay = Math.max(0, ...delayed.map((t) => getTaskDelayDays(t)));
          return { ...p, delayedCount: delayed.length, maxDelay };
        })
        .filter((p) => p.delayedCount > 0)
        .sort((a, b) => b.maxDelay - a.maxDelay),
    [projects]
  );

  // ── Top 5 lists ───────────────────────────────────────────────────────────

  const top5Requests = useMemo(
    () => [...requests].sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1)).slice(0, 5),
    [requests]
  );

  const top5Employees = useMemo(() => employees.slice(0, 5), [employees]);

  const recentClients = useMemo(
    () =>
      [...clients]
        .sort((a, b) => ((b.createdAt ?? "") > (a.createdAt ?? "") ? 1 : -1))
        .slice(0, 5),
    [clients]
  );

  const top5Projects = useMemo(
    () =>
      [...projects]
        .map((p) => ({ ...p, summary: getProjectProgressSummary(p) }))
        .sort((a, b) => b.summary.averageActual - a.summary.averageActual)
        .slice(0, 5),
    [projects]
  );

  const isLoading = loadingProjects;

  const [showFinancials, setShowFinancials] = useState(false);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <section className="admin-page">
      {errorMessage && <div className="alert alert-danger mb-2">{errorMessage}</div>}
      {isLoading && <div className="alert alert-light border">Loading dashboard data…</div>}

      {/* ── Project KPI Cards — 5 flat solid-color cards in one row ─────── */}
      <div className="dash-kpi-grid dash-kpi-grid--5col">
        <StatCard
          label="Active Projects"
          value={loadingProjects ? "…" : inProgressProjects}
          sub={`of ${projects.length} total`}
          icon="bi bi-kanban-fill"
          color="blue"
          flat
        />
        <StatCard
          label="Completed Projects"
          value={loadingProjects ? "…" : completedProjects}
          sub={`${projects.length > 0 ? Math.round((completedProjects / projects.length) * 100) : 0}% done`}
          icon="bi bi-check-circle-fill"
          color="green"
          flat
        />
        <StatCard
          label="Pending Projects"
          value={loadingProjects ? "…" : pendingProjects}
          sub="awaiting start"
          icon="bi bi-hourglass-split"
          color="gold"
          flat
        />
        <StatCard
          label="Overdue Tasks"
          value={loadingProjects ? "…" : delayedTasks.length}
          sub={`${delayedProjects.length} project${delayedProjects.length !== 1 ? "s" : ""} affected`}
          icon="bi bi-exclamation-triangle-fill"
          color="red"
          flat
        />
        <StatCard
          label="Project Cost"
          value={loadingProjects ? "…" : formatCrore(totalBudget)}
          sub={`${formatCrore(earnedBudget)} earned`}
          icon="bi bi-currency-rupee"
          color="violet"
          flat
          masked={!showFinancials}
          onToggleMask={() => setShowFinancials((v) => !v)}
        />
      </div>

      {/* ── General KPI Cards — flat solid color (matches row above) ─────── */}
      <div className="dash-kpi-grid dash-kpi-grid--5col">
        <StatCard
          label="Total Requests"
          value={loadingRequests ? "…" : requests.length}
          sub={`${requests.filter((r) => r.status === "pending").length} pending`}
          icon="bi bi-inbox-fill"
          color="orange"
          flat
        />
        <StatCard
          label="Active Clients"
          value={loadingClients ? "…" : clients.filter((c) => c.isActive).length}
          sub={`${clients.length} total`}
          icon="bi bi-people-fill"
          color="teal"
          flat
        />
        <StatCard
          label="Total Employees"
          value={loadingEmployees ? "…" : employees.length}
          sub={`${employees.filter((e) => e.status === "active").length} active`}
          icon="bi bi-person-badge-fill"
          color="indigo"
          flat
        />
        <StatCard
          label="Total Reviews"
          value={loadingReviews ? "…" : reviews.length}
          sub={`${reviews.filter((r) => r.status === "approved").length} approved`}
          icon="bi bi-star-fill"
          color="pink"
          flat
        />
        <StatCard
          label="Total Users"
          value={loadingEmployees || loadingClients ? "…" : employees.length + clients.length}
          sub={`${employees.length} staff · ${clients.length} clients`}
          icon="bi bi-person-lines-fill"
          color="emerald"
          flat
        />
      </div>

      {/* ── Progress Chart + Status Donut ──────────────────────────────────── */}
      <div className="row g-4">
        <div className="col-lg-8">
          <div className="dash-card h-100">
            <div className="dash-card-header">
              <div>
                <h2 className="dash-card-title">Project-wise Progress</h2>
                <p className="dash-card-sub">Planned vs actual completion percentage per project</p>
              </div>
              <Link to="/admin/projects/progress-analytics" className="btn btn-outline-secondary btn-sm">
                Full Analysis
              </Link>
            </div>
            {projects.length === 0 && !isLoading ? (
              <p className="text-muted small p-3">No project data available yet.</p>
            ) : (
              <ReactApexChart type="bar" options={progressChartOptions} series={progressChartSeries} height={280} />
            )}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="dash-card h-100">
            <div className="dash-card-header">
              <div>
                <h2 className="dash-card-title">Project Status</h2>
                <p className="dash-card-sub">Distribution across all projects</p>
              </div>
            </div>
            {projects.length === 0 && !isLoading ? (
              <p className="text-muted small p-3">No data.</p>
            ) : (
              <ReactApexChart type="donut" options={statusDonutOptions} series={statusDonutSeries} height={280} />
            )}
          </div>
        </div>
      </div>

      {/* ── Profit & Loss ─────────────────────────────────────────────────── */}
      <div className="row g-4">
        <div className="col-lg-8">
          <div className="dash-card h-100">
            <div className="dash-card-header">
              <div>
                <h2 className="dash-card-title">Profit &amp; Loss — Project wise</h2>
                <p className="dash-card-sub">Contract value vs earned value (based on actual completion)</p>
              </div>
            </div>
            {budgetProjects.length === 0 ? (
              <p className="text-muted small p-3">No budget data — add area and rate to your projects.</p>
            ) : (
              <ReactApexChart
                type="bar"
                options={plChartOptions}
                series={plChartSeries}
                height={Math.max(220, budgetProjects.length * 48)}
              />
            )}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="dash-card h-100">
            <div className="dash-card-header">
              <div>
                <h2 className="dash-card-title">Financial Summary</h2>
                <p className="dash-card-sub">Aggregated across all projects</p>
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowFinancials((v) => !v)}
                title={showFinancials ? "Hide figures" : "Show figures"}
              >
                <i className={`bi bi-eye${showFinancials ? "-slash" : ""}`}></i>
              </button>
            </div>
            <div className="dash-pl-summary">
              <div className="dash-pl-row">
                <span className="dash-pl-label">Total Contract Value</span>
                <span className="dash-pl-value dash-pl-value--total">
                  {showFinancials ? formatCrore(totalBudget) : "₹ •••••"}
                </span>
              </div>
              <div className="dash-pl-row">
                <span className="dash-pl-label">Earned Value</span>
                <span className="dash-pl-value dash-pl-value--green">
                  {showFinancials ? formatCrore(earnedBudget) : "₹ •••••"}
                </span>
              </div>
              <div className="dash-pl-row">
                <span className="dash-pl-label">Remaining / At Risk</span>
                <span className="dash-pl-value dash-pl-value--red">
                  {showFinancials ? formatCrore(totalBudget - earnedBudget) : "₹ •••••"}
                </span>
              </div>

              <div className="dash-pl-progress-wrap">
                <div className="dash-pl-progress-header">
                  <span className="text-muted">Overall Execution</span>
                  <strong>{overallPct}%</strong>
                </div>
                <ProgressBar pct={overallPct} variant="bg-success" size="md" label="Overall project execution progress" />
              </div>

              <div className="dash-pl-project-list mt-3">
                {budgetProjects.map((p) => {
                  const projectPct = p.budget > 0 ? (p.earned / p.budget) * 100 : 0;
                  return (
                    <div key={p.title} className="dash-pl-project-row">
                      <div className="dash-pl-project-header">
                        <span className="dash-pl-project-name">{p.title}</span>
                        <span className={`badge rounded-pill dash-badge-nowrap ${getStatusBadge(p.status)}`}>
                          {p.status}
                        </span>
                      </div>
                      <div className="dash-pl-project-amounts">
                        <span>{showFinancials ? formatCrore(p.earned) : "•••"}</span>
                        <span>of {showFinancials ? formatCrore(p.budget) : "•••"}</span>
                      </div>
                      <ProgressBar pct={projectPct} variant="bg-primary" size="xs" label={`${p.title} earned value progress`} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Delayed Projects ────────────────────────────────────────────────── */}
      <div className="dash-card">
        <div className="dash-card-header">
          <div>
            <h2 className="dash-card-title">
              <span className="badge text-bg-danger rounded-pill dash-badge-count me-2">
                {delayedProjects.length}
              </span>
              Delayed Projects
            </h2>
            <p className="dash-card-sub">Projects with at least one overdue task module</p>
          </div>
          <Link to="/admin/projects/progress" className="btn btn-outline-secondary btn-sm">
            Manage Progress
          </Link>
        </div>
        {delayedProjects.length === 0 ? (
          <div className="dash-empty-state">
            <i className="bi bi-check2-circle fs-2 text-success d-block mb-2"></i>
            <span>No delayed projects — everything is on track.</span>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Owner</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Delayed Tasks</th>
                  <th>Max Delay</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {delayedProjects.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div className="fw-bold">{p.title}</div>
                      <div className="small text-muted">{p.area} · {p.rateOfConstruction}</div>
                    </td>
                    <td>{p.owner || "—"}</td>
                    <td>{p.location || "—"}</td>
                    <td>
                      <span className={`badge rounded-pill ${getStatusBadge(p.status)}`}>{p.status}</span>
                    </td>
                    <td>
                      <span className="badge text-bg-danger rounded-pill">
                        {p.delayedCount} task{p.delayedCount !== 1 ? "s" : ""}
                      </span>
                    </td>
                    <td>
                      <span className="fw-semibold text-danger">
                        {p.maxDelay} day{p.maxDelay !== 1 ? "s" : ""}
                      </span>
                    </td>
                    <td>
                      <Link to="/admin/projects/progress" className="btn btn-outline-primary btn-sm">
                        <i className="bi bi-arrow-right-circle me-1"></i>Fix
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Three Lists Row ─────────────────────────────────────────────────── */}
      <div className="row g-4">
        {/* Top 5 Requests */}
        <div className="col-lg-4">
          <div className="dash-card h-100">
            <div className="dash-card-header">
              <div>
                <h2 className="dash-card-title">Top 5 Requests</h2>
                <p className="dash-card-sub">Most recent enquiries</p>
              </div>
              <Link to="/admin/requests" className="btn btn-outline-secondary btn-sm">View all</Link>
            </div>
            {loadingRequests ? (
              <p className="text-muted small px-1">Loading…</p>
            ) : top5Requests.length === 0 ? (
              <div className="dash-empty-state">No requests yet.</div>
            ) : (
              <div className="dash-list">
                {top5Requests.map((req, idx) => (
                  <div key={req.id} className="dash-list-item">
                    <div className="dash-list-rank">{idx + 1}</div>
                    <div className="flex-grow-1 min-w-0">
                      <div className="fw-semibold text-truncate">{req.name || "Unknown"}</div>
                      <div className="small text-muted text-truncate">
                        {req.projectType || "General"} · {req.city || "—"}
                      </div>
                      <div className="small text-muted">{req.createdAt || "—"}</div>
                    </div>
                    <span className={`badge rounded-pill dash-badge-nowrap ${getRequestStatusBadge(req.status)}`}>
                      {req.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Top 5 Employees */}
        <div className="col-lg-4">
          <div className="dash-card h-100">
            <div className="dash-card-header">
              <div>
                <h2 className="dash-card-title">Top 5 Users</h2>
                <p className="dash-card-sub">Registered team members</p>
              </div>
              <Link to="/admin/employees/new" className="btn btn-outline-secondary btn-sm">Add</Link>
            </div>
            {loadingEmployees ? (
              <p className="text-muted small px-1">Loading…</p>
            ) : top5Employees.length === 0 ? (
              <div className="dash-empty-state">No employees added yet.</div>
            ) : (
              <div className="dash-list">
                {top5Employees.map((emp, idx) => (
                  <div key={emp.id} className="dash-list-item">
                    <div className="dash-list-rank">{idx + 1}</div>
                    <div className="dash-avatar">
                      {getInitials(`${emp.firstName} ${emp.lastName}`)}
                    </div>
                    <div className="flex-grow-1 min-w-0">
                      <div className="fw-semibold text-truncate">{emp.firstName} {emp.lastName}</div>
                      <div className="small text-muted text-truncate">
                        {emp.department || "—"} · {emp.role || "—"}
                      </div>
                    </div>
                    <span className={`badge rounded-pill dash-badge-nowrap ${emp.status === "active" ? "text-bg-success" : "text-bg-secondary"}`}>
                      {emp.status || "active"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recently Added Clients */}
        <div className="col-lg-4">
          <div className="dash-card h-100">
            <div className="dash-card-header">
              <div>
                <h2 className="dash-card-title">Recent Clients</h2>
                <p className="dash-card-sub">Newly created client accounts</p>
              </div>
              <Link to="/admin/clients" className="btn btn-outline-secondary btn-sm">Manage</Link>
            </div>
            {loadingClients ? (
              <p className="text-muted small px-1">Loading…</p>
            ) : recentClients.length === 0 ? (
              <div className="dash-empty-state">No clients created yet.</div>
            ) : (
              <div className="dash-list">
                {recentClients.map((client, idx) => (
                  <div key={client.id} className="dash-list-item">
                    <div className="dash-list-rank">{idx + 1}</div>
                    <div className="dash-avatar dash-avatar--blue">
                      {getInitials(client.name)}
                    </div>
                    <div className="flex-grow-1 min-w-0">
                      <div className="fw-semibold text-truncate">{client.name}</div>
                      <div className="small text-muted text-truncate">{client.email}</div>
                      <div className="small text-muted">{client.createdAt || "—"}</div>
                    </div>
                    <span className={`badge rounded-pill dash-badge-nowrap ${client.isActive ? "text-bg-success" : "text-bg-secondary"}`}>
                      {client.isActive ? "Active" : "Off"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Top 5 Projects by Completion ─────────────────────────────────── */}
      <div className="dash-card">
        <div className="dash-card-header">
          <div>
            <h2 className="dash-card-title">Top 5 Projects</h2>
            <p className="dash-card-sub">Ranked by actual completion percentage</p>
          </div>
          <Link to="/admin/projects" className="btn btn-outline-secondary btn-sm">All Projects</Link>
        </div>
        {isLoading ? (
          <p className="text-muted small p-3">Loading…</p>
        ) : top5Projects.length === 0 ? (
          <div className="dash-empty-state">
            No projects yet. <Link to="/admin/projects/new">Create one</Link>.
          </div>
        ) : (
          <div className="dash-top-projects">
            {top5Projects.map((p, idx) => {
              const budget = parseBudget(p.area, p.rateOfConstruction);
              return (
                <div key={p.id} className="dash-top-project-row">
                  <div className="dash-top-project-rank">{idx + 1}</div>
                  <div className="flex-grow-1 min-w-0">
                    <div className="dash-top-project-meta">
                      <div>
                        <span className="fw-bold">{p.title}</span>
                        <span className="text-muted small ms-2">{p.location}</span>
                      </div>
                      <div className="dash-top-project-badges">
                        {budget > 0 && <span className="small text-muted">{formatCrore(budget)}</span>}
                        <span className={`badge rounded-pill ${getStatusBadge(p.status)}`}>{p.status}</span>
                      </div>
                    </div>

                    <div className="dash-top-project-progress">
                      <div className="flex-grow-1">
                        <div className="dash-top-project-pct-row">
                          <span>Planned {p.summary.averagePlanned}%</span>
                          <span className="fw-semibold text-dark">Actual {p.summary.averageActual}%</span>
                        </div>
                        {/* Dual-layer progress: planned (ghost) behind actual */}
                        <div className="dash-progress-dual-wrap">
                          <ProgressBar
                            pct={p.summary.averagePlanned}
                            variant="bg-primary dash-progress-ghost"
                            size="sm"
                            label={`${p.title} planned progress`}
                          />
                          <ProgressBar
                            pct={p.summary.averageActual}
                            variant="bg-success"
                            size="sm"
                            label={`${p.title} actual progress`}
                          />
                        </div>
                      </div>
                      <div className="dash-top-project-tasks small text-muted">
                        {p.summary.totalTasks} task{p.summary.totalTasks !== 1 ? "s" : ""}
                        {p.summary.delayedTasks > 0 && (
                          <span className="text-danger ms-1">· {p.summary.delayedTasks} delayed</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Quick Actions ───────────────────────────────────────────────────── */}
      <div className="dash-card">
        <h2 className="admin-panel-title mb-1">Quick Actions</h2>
        <p className="admin-panel-subtitle mb-4">Jump to key management areas from here.</p>
        <div className="admin-quick-links">
          <Link to="/admin/requests" className="admin-quick-link">
            <strong>Manage Requests</strong>
            <span>Update inquiry status and keep submissions organized.</span>
          </Link>
          <Link to="/admin/projects" className="admin-quick-link">
            <strong>Manage Projects</strong>
            <span>Track owner, location, area, and contract details.</span>
          </Link>
          <Link to="/admin/projects/progress" className="admin-quick-link">
            <strong>Update Task Progress</strong>
            <span>Add module-wise progress and capture delayed tasks.</span>
          </Link>
          <Link to="/admin/projects/progress-analytics" className="admin-quick-link">
            <strong>Progress Analytics</strong>
            <span>Compare planned vs actual and spot execution gaps.</span>
          </Link>
          <Link to="/admin/clients" className="admin-quick-link">
            <strong>Client Accounts</strong>
            <span>Create credentials and manage portal access permissions.</span>
          </Link>
          <Link to="/admin/project-images" className="admin-quick-link">
            <strong>Project Images</strong>
            <span>Add gallery assets and manage project visuals.</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default AdminDashboard;
